import { useRef, useState, useCallback, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';

interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: ReactNode;
  disabled?: boolean;
}

const PULL_THRESHOLD = 60;
const MAX_PULL = 100;

const SpinnerContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'hidden',
  transition: 'height 0.2s ease',
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
  color: theme.palette.text.secondary,
}));

export function PullToRefresh({ onRefresh, children, disabled = false }: PullToRefreshProps) {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const touchStartY = useRef(0);
  const isPulling = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // Ref-tracked pull distance for threshold check in handleTouchEnd — avoids stale closure
  const pullDistanceRef = useRef(0);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (disabled || isRefreshing) return;
      // Only start pull if scrolled to top
      const scrollTop = containerRef.current?.scrollTop ?? 0;
      if (scrollTop > 0) return;
      touchStartY.current = e.touches[0].clientY;
      isPulling.current = true;
    },
    [disabled, isRefreshing]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isPulling.current || disabled || isRefreshing) return;
      const deltaY = e.touches[0].clientY - touchStartY.current;
      if (deltaY <= 0) {
        setPullDistance(0);
        return;
      }
      // Dampen pull distance
      const dampened = Math.min(deltaY * 0.4, MAX_PULL);
      pullDistanceRef.current = dampened;
      setPullDistance(dampened);
    },
    [disabled, isRefreshing]
  );

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling.current || disabled || isRefreshing) return;
    isPulling.current = false;

    // Use ref to avoid stale closure — state may not reflect the final touch position
    if (pullDistanceRef.current >= PULL_THRESHOLD) {
      pullDistanceRef.current = 0;
      setIsRefreshing(true);
      setPullDistance(PULL_THRESHOLD);
      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
      }
    } else {
      pullDistanceRef.current = 0;
      setPullDistance(0);
    }
  }, [onRefresh, disabled, isRefreshing]);

  const progress = isRefreshing
    ? undefined // indeterminate
    : Math.min((pullDistance / PULL_THRESHOLD) * 100, 100);

  const spinnerHeight = isRefreshing ? PULL_THRESHOLD : pullDistance;

  return (
    <Box
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <SpinnerContainer sx={{ height: spinnerHeight > 0 ? spinnerHeight : 0 }}>
        {spinnerHeight > 10 && (
          <CircularProgress
            size={24}
            variant={isRefreshing ? 'indeterminate' : 'determinate'}
            value={progress}
            sx={{ opacity: isRefreshing ? 1 : pullDistance / PULL_THRESHOLD }}
          />
        )}
      </SpinnerContainer>
      {children}
    </Box>
  );
}
